import React from 'react';
import { toast } from 'react-toastify';
import { searchImputations } from '../../services/imputation';

/**
 * Permet de connaitre les exercices sur lesquels le service n'a aucune imputation
 * (aucun espace budgetaire, ou aucune ligne d'espace)
 * @returns Les identifiants des exercices sur lesquels le service ne peut pas imputer
 */
const useExercicesSansEspace = (
	identifiantService: number | null,
	identifiantsExercice: number[],
) => {
	// Exercices sans imputation, par service
	const [exercicesSansEspaceByService, setExercicesSansEspaceByService] =
		React.useState<Map<number, Set<number>>>(new Map());
	// Couples service/exercice deja demandes, pour ne pas relancer les requetes en cours
	const requestedKeys = React.useRef(new Set<string>());

	React.useEffect(() => {
		if (!identifiantService) return;

		identifiantsExercice.forEach(async (identifiantExercice) => {
			const key = `${identifiantService}-${identifiantExercice}`;

			if (requestedKeys.current.has(key)) return;

			requestedKeys.current.add(key);

			try {
				// Le filtre par service passe par les lignes d'espace : un service avec un espace sans ligne n'a aucune imputation
				const { meta } = await searchImputations(
					new URLSearchParams({
						identifiantExercice: String(identifiantExercice),
						identifiantService: String(identifiantService),
						perPage: '1',
					}),
				);

				if (meta.total > 0) return;

				setExercicesSansEspaceByService((old) =>
					new Map(old).set(
						identifiantService,
						new Set(old.get(identifiantService)).add(identifiantExercice),
					),
				);
			} catch {
				requestedKeys.current.delete(key);
				toast.error(
					'Erreur lors de la vérification des espaces budgétaires du service',
				);
			}
		});
	}, [identifiantService, identifiantsExercice]);

	// La reference ne change que si le service ou le resultat des verifications change
	return React.useMemo(
		() =>
			exercicesSansEspaceByService.get(identifiantService) ?? new Set<number>(),
		[exercicesSansEspaceByService, identifiantService],
	);
};

export default useExercicesSansEspace;
